
import { ReactNode, useState, useEffect, createContext, useContext } from "react";
import { Navbar } from "@/components/layout/Navbar";
import Sidebar from "@/components/layout/Sidebar"; // Desktop sidebar
import { MobileSheetSidebar } from "@/components/layout/MobileSheetSidebar"; // Mobile sidebar
import { ChatButton } from "@/components/chat/ChatButton";
import { AuthDebugPanel } from "@/components/layout/AuthDebugPanel";
import { Outlet, useLocation } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile"; // Import useIsMobile
import { usePageTracking } from '@/hooks/usePageTracking'; // Import the hook
import { useAuth } from '@/contexts/AuthContext';

interface AppLayoutProps {
  children?: ReactNode;
}

interface AppLayoutContextType {
  sidebarCollapsed: boolean; // For desktop sidebar state
  toggleSidebar: () => void; // Smart toggle for desktop collapse / mobile sheet
  isMobileSheetOpen: boolean; // For mobile sheet state
  closeMobileSheet: () => void; // Explicitly close mobile sheet
}

const AppLayoutContext = createContext<AppLayoutContextType | undefined>(undefined);

export const useAppLayout = () => {
  const context = useContext(AppLayoutContext);
  if (context === undefined) {
    throw new Error("useAppLayout must be used within an AppLayoutProvider (i.e. AppLayout component)");
  }
  return context;
};

const SIDEBAR_COLLAPSED_KEY = "invenio-sidebar-collapsed";

const AppLayout = ({ children }: AppLayoutProps) => {
  usePageTracking(); // Add the hook here where router context is available
  
  const { isAuthenticated, session } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => { // For desktop
    const savedState = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    return savedState === "true";
  });
  const [isMobileSheetOpen, setIsMobileSheetOpen] = useState(false); // For mobile
  const [showDebugPanel, setShowDebugPanel] = useState(false);

  const isMobile = useIsMobile(); // Hook to check if mobile
  const location = useLocation(); // For closing sheet on route change

  // Debug panel toggle (Ctrl+Shift+D)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'D') {
        setShowDebugPanel(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed.toString());
  }, [sidebarCollapsed]);

  // Close mobile sheet on route change
  useEffect(() => {
    if (isMobileSheetOpen) {
      setIsMobileSheetOpen(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);
  
  const toggleSidebar = () => {
    if (isMobile) {
      setIsMobileSheetOpen(prev => !prev);
    } else {
      setSidebarCollapsed(prev => !prev);
    }
  };

  const closeMobileSheet = () => {
    setIsMobileSheetOpen(false);
  };
  
  const contextValue: AppLayoutContextType = {
    sidebarCollapsed,
    toggleSidebar,
    isMobileSheetOpen,
    closeMobileSheet,
  };

  // Enhanced logging for debugging
  console.log('[AppLayout] Rendering with state:', {
    isAuthenticated,
    hasSession: !!session,
    pathname: location.pathname,
    sidebarCollapsed,
    isMobileSheetOpen
  });

  // Only render layout if user is authenticated with a valid session
  if (!isAuthenticated || !session) {
    console.log('[AppLayout] Not rendering layout - user not authenticated or no session');
    return <>{children ? children : <Outlet />}</>;
  }

  return (
    <AppLayoutContext.Provider value={contextValue}>
      <div className="flex min-h-screen flex-col bg-background">
        <Navbar />
        <div className="flex flex-1">
          <Sidebar collapsed={sidebarCollapsed} toggleSidebar={toggleSidebar} /> {/* Desktop Sidebar */}
          <MobileSheetSidebar 
            isOpen={isMobileSheetOpen} 
            onOpenChange={setIsMobileSheetOpen}
            onLinkClick={closeMobileSheet} // Ensure links in mobile sheet close it
          /> {/* Mobile Sheet Sidebar */}
          <div className={`flex-1 p-4 w-full overflow-y-auto transition-all duration-300`}>
            {children ? children : <Outlet />}
          </div>
        </div>
        <ChatButton />
        <AuthDebugPanel show={showDebugPanel} />
      </div>
    </AppLayoutContext.Provider>
  );
};

export default AppLayout;
